import { useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '../config/firebase';
import i18n from 'i18next'; // Import i18next to get current language

interface InvitePayload {
  emails: string[];
  brandId: string;
  role: string;
  // language is derived internally
}

interface InviteResponse {
  success: boolean;
  // Add other potential response fields if needed, e.g., message or details
  message?: string;
}

// Define the mutation function
const inviteTeamMembers = async (payload: InvitePayload): Promise<InviteResponse> => {
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('Authentication token is missing.');
  }

  const language = i18n.language || 'en'; // Get current language or default to 'en'

  const apiPayload = {
    ...payload,
    language: language,
  };

  const response = await fetch('https://api.socialness.ai/management/teams/invitations/create?fresh=true', {
    method: 'POST',
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(apiPayload),
  });

  const responseData = await response.json();

  if (!response.ok || !responseData.success) {
    const errorMessage = responseData.message || `Failed to send invitations (status: ${response.status})`;
    throw new Error(errorMessage);
  }

  return responseData;
};

// Create the custom hook using useMutation
export const useInviteTeamMembers = () => {
  const queryClient = useQueryClient();

  return useMutation<InviteResponse, Error, InvitePayload>({
    mutationFn: inviteTeamMembers,
    onSuccess: (data) => {
      console.log('Invitations sent successfully:', data);
      // Invalidate the team members query to refetch the list
      queryClient.invalidateQueries({ queryKey: ['teamMembers'] });
      // Optionally return data or handle success notification here
    },
    onError: (error) => {
      console.error('Error sending invitations:', error);
      // Handle error display to the user (e.g., using a toast library)
      // The error object can be used in the component where the mutation is called
    },
  });
}; 